<script lang="ts">
  import { onMount } from "svelte"
  import { Loader } from "lucide-svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import { xhr, toast } from "$app/utils"
  import Loading from "$app/components/Loading.svelte"
  import DatePicker from "$app/components/DatePicker.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let errors: Record<string, string[]> = $state({})
  interface Props {
    goal: GoalType
    onduplicate: (goal: GoalType) => void
  }

  let { goal, onduplicate }: Props = $props()

  let form = {
    ...goal,
    status: "on_track",
    name: `Duplicated ${goal.name}`,
    expected_at: goal.expected_at
  } as GoalType
  let expected_at = $state(null)
  try {
    expected_at = new Date(`${goal.expected_at}T00:00:00`)
  } catch (e) {}

  let goalTasks: TaskType[] & { expanded?: boolean } = $state([])
  let annual_goals: GoalType[] = $state([])
  let select_all = $state(false)

  async function loadAnnualGoals(): Promise<void> {
    try {
      const { data } = await xhr.get(
        `/plans/${goal.plan_id}/annual_goals?expected_at=${expected_at}`
      )
      annual_goals = data.goals
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("Annual Goals could not be loaded", "error")
    }
  }

  async function loadTasks(): Promise<void> {
    try {
      const { data } = await xhr.get<{ tasks: TaskType[] }>(
        `/plans/${goal.plan_id}/goals/${goal.id}.json`
      )
      goalTasks = data.tasks.map((t) => {
        return { name: t.name, id: t.id, _selected: false }
      }) as TaskType[] & { expanded?: boolean }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("Tasks could not be loaded", "error")
    }
  }

  let promiseTasks = loadTasks()

  let saving = $state(false)
  async function duplicateGoal(): Promise<void> {
    try {
      let task_ids = goalTasks.filter((t) => t._selected).map((v) => v.id)

      saving = true
      const resp: any = await xhr.post(
        `/plans/${goal.plan_id}/quarterly_goals/duplicate`,
        {
          id: goal.id,
          page: appStore.page,
          goal: {
            ...form,
            task_ids: task_ids
          }
        }
      )

      const newGoal: GoalType = resp.data
      const newTasks: TaskType[] = resp.data.tasks
      appStore.goals = [...appStore.goals, newGoal]
      appStore.tasks = [...appStore.tasks, ...newTasks]
      onduplicate(resp.data)
    } catch (e: any) {
      toast("There was an error duplicating the goal", "error")
      if (e.response.data) {
        errors = e.response.data.errors
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  function toggleSelectTasks() {
    goalTasks.forEach((task) => (task._selected = !select_all))
    goalTasks = goalTasks
  }

  onMount(() => {
    loadAnnualGoals()
  })
</script>

<div>
  <h2 class="text-2xl text-gray-500">Duplicate Quarterly Goal</h2>

  <div class="grid grid-cols-2 gap-4">
    <div>
      <label class="mb-1" for="name">Name</label>
      <div class="input-cont" class:error={errors.name}>
        <input
          type="text"
          data-name="goal-name"
          bind:value={form.name}
          class="input pt-2 pb-3"
        />
        <div class="msg">{errors.name}</div>
      </div>
    </div>

    <div>
      <label class="mb-1" for="name">Parent</label>
      <div class="input-cont" class:error={errors.parent_goal_id}>
        <select id="parent_goal_id" bind:value={form.parent_goal_id}>
          <option value="">Select Parent Goal</option>
          {#each annual_goals as annual_goal}
            <option value={annual_goal.id}>{annual_goal.name}</option>
          {:else}
            <option value="">No Annual Goals for current year</option>
          {/each}
        </select>
        <div class="msg">{errors.parent_goal_id}</div>
      </div>
    </div>

    <div>
      <label for="completed" class="label">Status:</label>
      <EditGoalStatus
        type="Goal"
        goalOrTask={form}
        update={false}
        onupdate={(t) => (form.status = t.status)}
      />
    </div>

    <div class="relative z-10">
      <label for="completed" class="label">Expected By:</label>
      <DatePicker
        date={expected_at}
        onchange={(detail) => {
          form.expected_at = detail
        }}
      />
    </div>
  </div>

  <div class="my-8 border-b border-gray-400 pb-2" style="min-height: 10rem;">
    {#await promiseTasks}
      <div class="text-gray-500">
        <Loader class="rotate" />
        Loading Tasks
      </div>
    {:then}
      {#if goalTasks.length > 0}
        <h2
          class="text-xl text-gray-600 border-b border-gray-200 flex items-center"
        >
          Select Tasks to Copy to New Goal
        </h2>
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="flex justify-start items-center py-2 px-4 cursor-pointer mt-1 border-b border-gray-300"
          onclick={toggleSelectTasks}
        >
          <input type="checkbox" id="select-all" bind:checked={select_all} />
          <label for="select-all" class="ml-1 pt-0"
            ><strong>Select all</strong></label
          >
        </div>
        <ul class="ml-2 mt-2 overflow-y-auto max-h-72">
          {#each goalTasks as task, idx (task.id)}
            <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <li
              class="p-2 cursor-pointer mb-1"
              onclick={() => (task._selected = !task._selected)}
            >
              <input type="checkbox" bind:checked={task._selected} />
              {task.name}
            </li>
          {/each}
        </ul>
      {:else}
        <h2 class="text-xl text-gray-500">No Tasks defined for this goal</h2>
      {/if}
    {/await}
  </div>

  <div class="text-center md:text-right mt-4">
    <Loading loading={saving}>
      <button
        class="btn-rounded success btn-big w-64 h-14"
        onclick={duplicateGoal}
      >
        Duplicate
      </button>
    </Loading>
  </div>
</div>
